import React, { useState, useEffect } from 'react';
import { transactionsApi } from '../utils/api';
import { formatCurrency, formatDate } from '../utils/helpers';

const STORNO_REASONS = [
  'Fehlerhafte Bestellung',
  'Kundenreklamation',
  'Falsche Eingabe',
  'Produktmangel',
  'Sonstige Gründe'
];

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Storno Modal
  const [showStornoModal, setShowStornoModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [stornoType, setStornoType] = useState('full');
  const [stornoReason, setStornoReason] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [adminPin, setAdminPin] = useState('');
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const data = await transactionsApi.getAll({ limit: 100 });
      setTransactions(data.transactions || []);
      setError(null);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openStornoModal = (transaction) => {
    if (transaction.type === 'storno') {
      setMessage({ type: 'error', text: 'Ein Storno kann nicht storniert werden' });
      return;
    }
    setSelectedTransaction(transaction);
    setShowStornoModal(true);
    setStornoType('full');
    setStornoReason('');
    setSelectedItems([]);
    setAdminPin('');
  };

  const toggleItemSelection = (sku) => {
    if (selectedItems.includes(sku)) {
      setSelectedItems(selectedItems.filter(s => s !== sku));
    } else {
      setSelectedItems([...selectedItems, sku]);
    }
  };

  const handleStorno = async () => {
    // Validierung
    if (!stornoReason) {
      setMessage({ type: 'error', text: 'Bitte Stornogrund auswählen' });
      return;
    }

    if (stornoType === 'partial' && selectedItems.length === 0) {
      setMessage({ type: 'error', text: 'Bitte mindestens ein Item für Teilstorno auswählen' });
      return;
    }

    // Admin-PIN Validierung (Demo: "1234")
    if (adminPin !== '1234') {
      setMessage({ type: 'error', text: 'Ungültige Admin-PIN' });
      return;
    }

    try {
      setProcessing(true);
      
      const stornoData = {
        type: stornoType,
        reason: stornoReason,
        created_by: 'admin-001',
      };

      if (stornoType === 'partial') {
        stornoData.items_to_storno = selectedItems;
      }

      const result = await transactionsApi.storno(selectedTransaction.id, stornoData);
      
      setMessage({ 
        type: 'success', 
        text: `Storno erfolgreich erstellt: ${result.receipt_no}` 
      });
      
      setShowStornoModal(false);
      loadTransactions();
      
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Storno error:', err);
      setMessage({ type: 'error', text: 'Fehler beim Stornieren: ' + err.message });
    } finally {
      setProcessing(false);
    }
  };

  const getTypeBadge = (type, stornoType) => {
    const badges = {
      sale: { label: 'Verkauf', color: 'bg-green-100 text-[var(--success-text)]' },
      delivery: { label: 'Lieferung', color: 'bg-blue-100 text-blue-700' },
      pickup: { label: 'Abholung', color: 'bg-purple-100 text-purple-700' },
      storno: { 
        label: stornoType === 'full' ? 'Vollstorno' : 'Teilstorno', 
        color: 'bg-red-100 text-[var(--error-text)]' 
      },
    };
    const badge = badges[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const viewReceipt = (transactionId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/receipts/${transactionId}/html`, '_blank');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Bons / Transaktionen
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Alle Kassenvorgänge mit Storno-Funktion
          </p>
        </div>
        <button
          onClick={loadTransactions}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:opacity-90 transition-opacity text-sm font-medium"
          data-testid="refresh-transactions-button"
        >
          Aktualisieren
        </button>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-[var(--success-bg)] text-[var(--success-text)] border border-green-200' : 'bg-[var(--error-bg)] text-[var(--error-text)] border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-[var(--error-bg)] border border-red-200 text-[var(--error-text)] rounded-lg">
          {error}
        </div>
      )}

      <div className="dark-card rounded-lg shadow border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[var(--text-secondary)]">Lade Transaktionen...</div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center text-[var(--text-secondary)]">Keine Transaktionen vorhanden</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="transactions-table">
              <thead className="bg-[var(--bg-tertiary)] border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Bon-Nr
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Datum/Zeit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Quelle
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Brutto
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    TSE
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="dark-card divide-y divide-gray-200">
                {transactions.map((trans) => (
                  <tr key={trans.id} className="hover:bg-[var(--bg-tertiary)] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {trans.receipt_no}
                      {trans.original_transaction_id && (
                        <div className="text-xs text-[var(--text-secondary)] mt-1">
                          → Original: {transactions.find(t => t.id === trans.original_transaction_id)?.receipt_no || trans.original_transaction_id}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]">
                      {formatDate(trans.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(trans.type, trans.storno_type)}
                      {trans.storno_reason && (
                        <div className="text-xs text-[var(--text-secondary)] mt-1">{trans.storno_reason}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {trans.source === 'portal' ? (
                        <span className="text-[var(--accent-primary)]">Portal</span>
                      ) : (
                        <span>Kasse</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold">
                      {formatCurrency(trans.total_gross)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      {trans.tse_data ? (
                        <span className="text-green-600" title={`Signatur: ${trans.tse_data.signature_finish}`}>
                          ✓ #{trans.tse_data.signature_counter_finish}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-2">
                      <button
                        onClick={() => viewReceipt(trans.id)}
                        className="text-[var(--accent-primary)] hover:underline"
                        data-testid={`view-receipt-${trans.id}`}
                      >
                        Bon
                      </button>
                      {trans.type !== 'storno' && (
                        <button
                          onClick={() => openStornoModal(trans)}
                          className="text-red-600 hover:underline font-medium"
                          data-testid={`storno-${trans.id}`}
                        >
                          Stornieren
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Storno Modal */}
      {showStornoModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !processing && setShowStornoModal(false)}>
          <div className="dark-card rounded-lg shadow-xl p-6 w-[600px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold mb-4">Storno erstellen</h2>
            
            <div className="mb-4 p-3 bg-[var(--bg-tertiary)] rounded-lg">
              <div className="text-sm text-[var(--text-secondary)]">Original-Bon:</div>
              <div className="font-bold">{selectedTransaction.receipt_no}</div>
              <div className="text-sm text-[var(--text-secondary)]">Betrag: {formatCurrency(selectedTransaction.total_gross)}</div>
            </div>

            {/* Storno-Typ */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Storno-Typ:</label>
              <div className="flex space-x-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="full"
                    checked={stornoType === 'full'}
                    onChange={(e) => setStornoType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="font-medium">Vollstorno</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    value="partial"
                    checked={stornoType === 'partial'}
                    onChange={(e) => setStornoType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="font-medium">Teilstorno</span>
                </label>
              </div>
            </div>

            {/* Artikel-Auswahl bei Teilstorno */}
            {stornoType === 'partial' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Zu stornierende Artikel:</label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {selectedTransaction.items.map((item, idx) => (
                    <label key={idx} className="flex items-center p-3 bg-[var(--bg-tertiary)] rounded-lg cursor-pointer hover:bg-gray-100">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.sku)}
                        onChange={() => toggleItemSelection(item.sku)}
                        className="mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-[var(--text-secondary)]">{item.qty}x {formatCurrency(item.price_net * (1 + item.vat_rate / 100))}</div>
                      </div>
                      <div className="font-bold">{formatCurrency(item.total_gross)}</div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Stornogrund */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Stornogrund: <span className="text-red-600">*</span>
              </label>
              <select
                value={stornoReason}
                onChange={(e) => setStornoReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Bitte auswählen...</option>
                {STORNO_REASONS.map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            {/* Admin-PIN */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Admin-PIN: <span className="text-red-600">*</span>
              </label>
              <input
                type="password"
                value={adminPin}
                onChange={(e) => setAdminPin(e.target.value)}
                placeholder="Admin-PIN eingeben"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                maxLength="4"
              />
              <div className="text-xs text-[var(--text-secondary)] mt-1">Demo-PIN: 1234</div>
            </div>

            {/* Warnung */}
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
              ⚠️ Ein Storno kann nicht rückgängig gemacht werden. Der Vorgang wird TSE-signiert und im DSFinV-K Export dokumentiert.
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowStornoModal(false)}
                disabled={processing}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-semibold disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleStorno}
                disabled={processing || !stornoReason || !adminPin}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:opacity-90 disabled:opacity-50"
              >
                {processing ? 'Storniere...' : 'Storno erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Transactions;
